import { useMemo } from 'react'
import { motion } from 'framer-motion'
import Select from 'react-select'
import {
  ClipboardList,
  ListOrdered,
  Languages,
  FileText,
  WandSparkles,
  Info,
  TextQuote,
  Clock
} from 'lucide-react'

export default function CommonSettings({
  generateButtonDisabled,
  generateButtonLabel,
  onGenerateClick,
  showResetButton = false,
  onResetClick,
  KnowledgeUploaderEl,
  extraPrompt,
  setExtraPrompt,
  examType,
  setExamType,
  optionCount,
  setOptionCount,
  level,
  setLevel,
  optionStyle,
  setOptionStyle,
  questionCount,
  setQuestionCount,
  typeSummary,
  setTypeSummary,
  typeSummaryMin,
  setTypeSummaryMin,
  typeSummaryMax,
  setTypeSummaryMax,
  levelMapping,
  examTypeMapping,
  typeSummaryMapping,
  allowLanguage = false,
  examLanguage = '',
  setExamLanguage = () => { },
  langs = []
}) {
  const fadeUp = { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } }

  const langOptions = useMemo(
    () =>
      langs.map(l => ({
        value: l.name,
        label: `${l.flag ? String.fromCodePoint(...[...l.flag.split('-')[0].toUpperCase()].map(ch => 127397 + ch.charCodeAt())) + ' ' : ''}${l.label} (${l.name})`
      })),
    [langs]
  )

  const langValue = langOptions.find(o => o.value === examLanguage) || null

  const clampInt = (v, min, max) => {
    const n = parseInt(v, 10)
    if (Number.isNaN(n)) return min
    return Math.max(min, Math.min(max, n))
  }

  const handleNumberChange = (setter, min, max) => (e) => {
    const value = e.target.value;

    // อนุญาตแค่ตัวเลข 0-9 หรือค่าว่าง
    if (/^\d*$/.test(value)) {
      setter(value);
    }
  };

  const preventInvalidKeys = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };


  const handleQuestionChange = (e) => {
    let value = e.target.value;

    // กัน e + - .
    if (["e", "E", "+", "-", "."].includes(e.nativeEvent.data)) return;

    if (value === "") {
      setQuestionCount("");
      return;
    }

    let num = parseInt(value, 10);

    if (isNaN(num)) return;

    if (num > 100) num = 100;
    if (num < 1) num = 1;

    setQuestionCount(num);
  };


  const secsPerQuestion = 10.0;

  const toMinSec = (sec) => {
    const t = Math.round(sec);
    const m = Math.floor(t / 60);
    const s = t % 60;
    return { m, s };
  };

  const formatThai = (sec) => {
    const { m, s } = toMinSec(sec);
    return `${m} นาที ${s} วินาที`;
  };



  return (
    <motion.div {...fadeUp} className="flex flex-col gap-6">
      {KnowledgeUploaderEl}

      <div className="grid md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium flex items-center gap-1">
            <ClipboardList size={16} /> จำนวนข้อ  <span className='text-[11px] text-gray-500 inline-flex items-center gap-1'>  <Clock size={12} /> ใช้เวลาประมาณ : <span className='text-secondary'> {formatThai(questionCount * secsPerQuestion)} - {formatThai(questionCount * (secsPerQuestion * 1.5))} </span> </span>
          </label>
          <input
            type="number"
            min={1}
            max={100}
            value={questionCount}
            onChange={handleQuestionChange}
            onKeyDown={(e) => {
              if (["e", "E", "+", "-", "."].includes(e.key)) {
                e.preventDefault();
              }
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          />

          <p className="text-[11px] text-gray-500 inline-flex items-center gap-1">
            <Info size={12} /> แนะนำ: 5–20 ข้อ สำหรับแบบฝึกหัดสั้น, 20–40 ข้อสำหรับแบบทดสอบเต็ม (จำครั้งละไม่เกิน 100 ข้อ)
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium flex items-center gap-1">
            <ListOrdered size={16} /> จำนวนตัวเลือก
          </label>
          <input
            type="number"
            inputMode="numeric"
            min={2}
            max={8}
            value={optionCount}
            onChange={handleNumberChange(setOptionCount, 2, 8)}
            onKeyDown={preventInvalidKeys}
            onBlur={e => setOptionCount(clampInt(e.target.value, 2, 8))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          />
          <p className="text-[11px] text-gray-500 inline-flex items-center gap-1">
            <Info size={12} /> นิยม 4 ตัวเลือก (A–D) หรือ 5 ตัวเลือก (A–E)
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium flex items-center gap-1">
          <FileText size={16} /> คำสั่งเพิ่มเติมสำหรับ AI (Prompt)
        </label>
        <textarea
          rows={4}
          value={extraPrompt}
          onChange={e => setExtraPrompt(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 w-full text-sm focus:ring-2 focus:ring-primary"
          placeholder="ใส่รายละเอียดเพิ่มเติมเพื่อให้ AI สร้างข้อสอบได้ตรงใจขึ้น (เช่น ระดับ Bloom, ต้องการตัวลวง, โจทย์มีกรณีศึกษา ฯลฯ)"
        />
      </div>

      {allowLanguage && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium flex items-center gap-1">
            <Languages size={16} /> ภาษาในการออกข้อสอบ
          </label>
          <Select
            classNamePrefix="rs"
            options={langOptions}
            value={langValue}
            onChange={o => setExamLanguage(o?.value || '')}
            isClearable
            isSearchable
            placeholder="เลือกภาษา"
          />
        </div>
      )}

      <div className="flex items-center gap-3 mt-2">
        <button
          className="inline-flex items-center gap-2 bg-primary hover:bg-secondary disabled:opacity-50 text-white rounded-lg px-5 py-2 text-sm font-semibold"
          disabled={generateButtonDisabled}
          onClick={onGenerateClick}
        >
          <WandSparkles size={16} /> {generateButtonLabel}
        </button>

        {showResetButton && (
          <button
            onClick={onResetClick}
            className="inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-lg px-4 py-2 text-xs font-semibold"
          >
            รีเซ็ต/สร้างใหม่
          </button>
        )}
      </div>
    </motion.div>
  )
}
